/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import prefix from "loglevel-plugin-prefix";
import log from "loglevel";

prefix.reg(log);

// 🪵 INSTRUCTIONS 🪵
// To enable logs in Chrome, set the key "loglevel" to "TRACE" in local storage for the app.

// Define a type for our enhanced logger
export interface EnhancedLogger extends log.Logger {
  createSubLogger: (tag: string) => EnhancedLogger;
  tags: string[];
}

prefix.apply(log, {
  template: "[%n] %l:",
  levelFormatter: (level) => level.toUpperCase(),
  nameFormatter: (name) => {
    if (Array.isArray(name)) {
      return name.join("][");
    }
    return name || "General";
  },
});

// Enhance the logger with our sub-logger functionality
const enhanceLogger = (
  logger: log.Logger,
  tags: string[] = []
): EnhancedLogger => {
  const enhanced = logger as EnhancedLogger;
  enhanced.tags = tags;

  enhanced.createSubLogger = function (tag: string): EnhancedLogger {
    const newTags = [...this.tags, tag];
    // Convert tags array to string for getLogger
    const loggerName = newTags.join("][");
    return enhanceLogger(log.getLogger(loggerName), newTags);
  };

  return enhanced;
};

// Modified create logger function
export const createLogger = (name: string): EnhancedLogger => {
  return enhanceLogger(log.getLogger(name), [name]);
};
